package top.wcpe.allincore.ui.runtime.render.model

import kotlin.jvm.JvmStatic

/**
 * OS 鼠标指针形状（最小实用集）。
 *
 * 这是「悬停驱动」的元素级元数据：DSL 元素声明 `cursor="pointer"` 后，
 * ui-runtime 在悬停 dispatch 时解析为本枚举，经 `CursorBridge` 回调平台层，
 * 由 fabric 用 GLFW 标准指针设置 OS 光标。
 *
 * 注意：本枚举表达的是「OS 指针形状」，与拖拽中的「光标物品」（CursorState）
 * 以及服务端程序化移动光标的 warpCursor 是完全独立的概念，勿混淆。
 *
 * 部分形状（如 wait）在 GLFW 标准光标（约 10 种）里未必都有对应常量，
 * 平台实现层按实际可用裁剪、缺失项回退默认箭头。
 *
 * ADR-0072 决策 1 镜像：真源为 `platform-client-api` 的
 * `top.wcpe.allincore.platform.client.api.render.CursorType`
 * （纯 Java 8 对外 API，按 ADR-0002 不得 KMP 化）。值名、声明序与 [fromToken] 解析逻辑
 * 照抄真源，一致性由 jvmTest 的 RenderModelConsistencyTest 逐值 / 逐 token 守护。
 */
enum class CursorType {
    /** 默认箭头（未声明 cursor 或非法值时的兜底）。 */
    DEFAULT,

    /** 手型，常用于可点击元素。 */
    POINTER,

    /** I 形文本光标，用于可编辑文本。 */
    TEXT,

    /** 移动（四向）光标。 */
    MOVE,

    /** 水平双向缩放光标。 */
    RESIZE_H,

    /** 垂直双向缩放光标。 */
    RESIZE_V,

    /** 左上-右下对角缩放光标。 */
    RESIZE_NWSE,

    /** 右上-左下对角缩放光标。 */
    RESIZE_NESW,

    /** 禁止/不可用光标。 */
    NOT_ALLOWED,

    /** 等待/忙碌光标。 */
    WAIT,
    ;

    companion object {
        /**
         * 把 DSL 字符串取值解析为指针类型（纯函数）。
         *
         * 大小写不敏感、容忍首尾空白；`-` 视作 `_`（如 `"resize-h"` → [RESIZE_H]）。
         * 未知 / null / 空串一律降级为 [DEFAULT]，不抛异常。
         *
         * 与真源实现等价：真源经 `Locale.ROOT` 大写 + `valueOf` 捕获异常，此处用
         * 区域无关的 [uppercase] + 查表（commonMain 无 java.util.Locale），行为逐 token 一致。
         */
        @JvmStatic
        fun fromToken(token: String?): CursorType {
            if (token == null) {
                return DEFAULT
            }
            val normalized = token.trim().replace('-', '_').uppercase()
            if (normalized.isEmpty()) {
                return DEFAULT
            }
            return entries.firstOrNull { it.name == normalized } ?: DEFAULT
        }
    }
}
